/**
 * Analytics Service for Prodgy
 * 
 * Centralized service for tracking user interactions and events
 * Integrates with Google Tag Manager and GA4
 */

import { pushToDataLayer } from '@/shared/hooks/useGoogleTagManager';
import type { EventName, EventParameters } from './eventTypes';

// ============================================================================
// Analytics Service
// ============================================================================

class AnalyticsService {
  /**
   * Track a custom event with type-safe parameters
   */
  trackEvent<T extends EventName>(
    eventName: T,
    parameters: EventParameters[T]
  ): void {
    try {
      pushToDataLayer(eventName, parameters as Record<string, unknown>);
    } catch (error) {
      // Silent fail - analytics should never break the app
    }
  }

  // ==========================================================================
  // User Journey Events
  // ==========================================================================

  trackOnboardingCompleted(params: EventParameters['onboarding_completed']): void {
    this.trackEvent('onboarding_completed', params);
  }

  trackFirstProductCreated(params: EventParameters['first_product_created']): void {
    this.trackEvent('first_product_created', params);
  }

  trackUserLogin(): void {
    this.trackEvent('user_login', {});
  }

  trackUserLogout(): void {
    this.trackEvent('user_logout', {});
  }

  // ==========================================================================
  // Agent Events
  // ==========================================================================

  trackAgentInstalled(params: EventParameters['agent_installed']): void {
    this.trackEvent('agent_installed', params);
  }

  trackAgentInstallationFailed(params: EventParameters['agent_installation_failed']): void {
    this.trackEvent('agent_installation_failed', params);
  }

  trackAgentExecuted(params: EventParameters['agent_executed']): void {
    this.trackEvent('agent_executed', params);
  }

  trackAgentUninstalled(params: EventParameters['agent_uninstalled']): void {
    this.trackEvent('agent_uninstalled', params);
  }

  // ==========================================================================
  // Integration Events
  // ==========================================================================

  trackIntegrationAdded(params: EventParameters['integration_added']): void {
    this.trackEvent('integration_added', params);
  }

  trackIntegrationAddFailed(params: EventParameters['integration_add_failed']): void {
    this.trackEvent('integration_add_failed', params);
  }

  trackIntegrationTested(params: EventParameters['integration_tested']): void {
    this.trackEvent('integration_tested', params);
  }

  trackIntegrationRemoved(params: EventParameters['integration_removed']): void {
    this.trackEvent('integration_removed', params);
  }

  // ==========================================================================
  // Knowledge Base Events
  // ==========================================================================

  trackDocumentUploaded(params: EventParameters['document_uploaded']): void {
    this.trackEvent('document_uploaded', params);
  }

  trackDocumentUploadFailed(params: EventParameters['document_upload_failed']): void {
    this.trackEvent('document_upload_failed', params);
  }

  trackKnowledgeSearch(params: EventParameters['knowledge_search']): void {
    this.trackEvent('knowledge_search', params);
  }

  trackAIQueryNotebook(params: EventParameters['ai_query_notebook']): void {
    this.trackEvent('ai_query_notebook', params);
  }

  // ==========================================================================
  // Workflow Events
  // ==========================================================================

  trackWorkflowCreated(params: EventParameters['workflow_created']): void {
    this.trackEvent('workflow_created', params);
  }

  trackWorkflowExecuted(params: EventParameters['workflow_executed']): void {
    this.trackEvent('workflow_executed', params);
  }

  trackWorkflowExecutionFailed(params: EventParameters['workflow_execution_failed']): void {
    this.trackEvent('workflow_execution_failed', params);
  }

  // ==========================================================================
  // Playground Events
  // ==========================================================================

  trackPlaygroundInteraction(params: EventParameters['playground_interaction']): void {
    this.trackEvent('playground_interaction', params);
  }

  // ==========================================================================
  // Engagement Events
  // ==========================================================================

  trackFeatureDiscovered(params: EventParameters['feature_discovered']): void {
    this.trackEvent('feature_discovered', params);
  }

  trackEngagedSession(params: EventParameters['engaged_session']): void {
    this.trackEvent('engaged_session', params);
  }

  trackTourCompleted(params: EventParameters['tour_completed']): void {
    this.trackEvent('tour_completed', params);
  }

  trackTourSkipped(params: EventParameters['tour_skipped']): void {
    this.trackEvent('tour_skipped', params);
  }

  // ==========================================================================
  // Feedback Events
  // ==========================================================================

  trackFeedbackImpression(params: EventParameters['feedback_impression']): void {
    this.trackEvent('feedback_impression', params);
  }

  trackFeedbackSubmitted(params: EventParameters['feedback_submitted']): void {
    this.trackEvent('feedback_submitted', params);
  }

  trackFeedbackDismissed(params: EventParameters['feedback_dismissed']): void {
    this.trackEvent('feedback_dismissed', params);
  }

  // ==========================================================================
  // Utility Methods
  // ==========================================================================

  /**
   * Calculate duration in seconds from timestamp
   */
  getDurationSeconds(startTime: number): number {
    return Math.round((Date.now() - startTime) / 1000);
  }

  /**
   * Calculate duration in milliseconds from timestamp
   */
  getDurationMs(startTime: number): number {
    return Date.now() - startTime;
  }

  /**
   * Calculate file size in KB
   */
  getFileSizeKB(bytes: number): number {
    return Math.round(bytes / 1024);
  }

  /**
   * Calculate days between dates
   */
  getDaysBetween(startDate: Date, endDate: Date = new Date()): number {
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
}

// ============================================================================
// Export Singleton Instance
// ============================================================================

export const analyticsService = new AnalyticsService();
